import { 
  Box, 
  VStack, 
  HStack, 
  Text, 
  Button, 
  Heading, 
  Image, 
  Container, 
  SimpleGrid, 
  Icon, 
  useColorModeValue,
  Flex,
  Badge,
  Divider,
  Link,
  Table,
  Thead,
  Tr,
  Th,
  Tbody,
  Td
} from '@chakra-ui/react'
import { FaMicrophone, FaQrcode, FaCircle, FaUsers, FaChartLine, FaMobileAlt, FaArrowRight, FaApple, FaGooglePlay } from 'react-icons/fa'
import { useNavigate } from 'react-router-dom'
import { UserMenu } from './auth/UserMenu'
import { useState } from 'react'
import { QRCodeScanner } from './modals/QRCodeScanner'
import FeedbackModal from './modals/FeedbackModal'
import { BrowserCompatibilityWarning } from './common/BrowserCompatibilityWarning' // TODO: Ensure this component exists and is up to date
import { PWAInstallButton } from './common/PWAInstallButton'

interface LandingPageProps {
  onGetStarted: () => void
}

export function LandingPage({ onGetStarted }: LandingPageProps) {
  const navigate = useNavigate()
  const [isScanOpen, setScanOpen] = useState(false)
  const [isFeedbackOpen, setFeedbackOpen] = useState(false)
  const bgColor = useColorModeValue('#09090b', '#09090b')
  const textColor = useColorModeValue('white', 'white')
  const cardBg = useColorModeValue('#191919', '#191919')
  const accentColor = '#3182ce'

  const handleGetStarted = () => {
    onGetStarted()
  }

  const handleJoinMatch = () => {
    navigate('/?screen=join')
  }

  return (
    <Box minH="100vh" bg={bgColor} color={textColor}>
      {/* Browser Compatibility Warning (Phase 6) */}
      <BrowserCompatibilityWarning />
      {/* Header */}
      <Box px={{ base: 4, md: 6 }} py={4} borderBottom="1px" borderColor="gray.700">
        <Container maxW="container.xl" position="relative">
          {/* Desktop View */}
          <Box display={{ base: 'none', md: 'block' }}>
            {/* Left side - Join Match */}
            <Box position="absolute" left="0" top="50%" transform="translateY(-50%)">
              <Button
                size="lg"
                variant="outline"
                color={accentColor}
                borderColor={accentColor}
                _hover={{ bg: accentColor, color: 'white' }}
                onClick={() => setScanOpen(true)}
                px={8}
                py={6}
                fontSize="lg"
                borderRadius="2px"
              >
                Scan to Control
              </Button>
            </Box>

            {/* Center - Start New Match */}
            <Flex justify="center">
              <Button
                size="lg"
                colorScheme="blue"
                bg={accentColor}
                color="black"
                _hover={{ bg: '#2c5aa0' }}
                onClick={onGetStarted}
                px={8}
                py={6}
                fontSize="lg"
                borderRadius="2px"
              >
                Start New Match
              </Button>
            </Flex>

            {/* Right side - Pricing and User Menu */}
            <Box position="absolute" right="0" top="50%" transform="translateY(-50%)">
              <HStack spacing={4}>
                <Button
                  variant="ghost"
                  color={textColor}
                  _hover={{ bg: 'whiteAlpha.100' }}
                  onClick={() => navigate('/pricing')}
                  size="md"
                >
                  Pricing
                </Button>
                <UserMenu />
              </HStack>
            </Box>
          </Box>

          {/* Mobile View */}
          <Box display={{ base: 'flex', md: 'none' }} flexDirection="column" gap={2} w="full">
            {/* Mobile Pricing and Login */}
            <HStack spacing={2} justify="flex-end" w="full" mb={2}>
              <Button
                variant="ghost"
                color={textColor}
                _hover={{ bg: 'whiteAlpha.100' }}
                onClick={() => navigate('/pricing')}
                size="sm"
              >
                Pricing
              </Button>
              <UserMenu />
            </HStack>

            <Button
              size="md"
              colorScheme="blue"
              bg={accentColor}
              color="black"
              _hover={{ bg: '#2c5aa0' }}
              onClick={onGetStarted}
              px={6}
              py={4}
              fontSize="md"
              borderRadius="2px"
              w="full"
            >
              Start New Match
            </Button>
            <Button
              size="md"
              variant="outline"
              color={accentColor}
              borderColor={accentColor}
              _hover={{ bg: accentColor, color: 'white' }}
              onClick={() => setScanOpen(true)}
              px={6}
              py={4}
              fontSize="md"
              borderRadius="2px"
              w="full"
            >
              Scan to Control
            </Button>
          </Box>
        </Container>
      </Box>

      {/* Hero Section */}
      <Container maxW="container.xl" py={{ base: 12, md: 20 }}>
        <VStack spacing={{ base: 8, md: 12 }} textAlign="center">
          {/* Main Hero */}
          <VStack spacing={{ base: 6, md: 8 }} maxW="4xl">
            {/* PWA Install Button */}
            <PWAInstallButton />
            <VStack spacing={0} mb={0} mt={-4}>
              <Button
                size="lg"
                bg="#e2e700"
                color="black"
                _hover={{ bg: '#c8cd00' }}
                onClick={() => navigate('/pricing')}
                px={8}
                py={6}
                fontSize="xl"
                fontWeight="bold"
                borderRadius="3px"
              >
                Start Free Trial
              </Button>
              <Text fontSize="sm" color="gray.300" mt={2} fontStyle="italic">
                No download needed
              </Text>
            </VStack>
            <Button
              variant="outline"
              color={accentColor}
              borderColor={accentColor}
              _hover={{ bg: 'whiteAlpha.100' }}
              onClick={() => setFeedbackOpen(true)}
              size="sm"
              mb={4}
              borderRadius="3px"
            >
              Give us your Feedback
            </Button>
            <Badge 
              bg={accentColor}
              color="black"
              px={4} 
              py={2} 
              borderRadius="full"
              fontSize="sm"
              fontWeight="bold"
            >
              AI-POWERED SCORING
            </Badge>
            <Text fontSize={{ base: "sm", md: "md" }} color="blue.300" fontWeight="bold" letterSpacing="wide" mt={-2} mb={2} textAlign="center" px={2}>
              <Text as="span" display={{ base: "block", md: "inline" }}>PADEL</Text>
              <Text as="span" display={{ base: "none", md: "inline" }}>&nbsp;|&nbsp;</Text>
              <Text as="span" display={{ base: "block", md: "inline" }}>TENNIS</Text>
              <Text as="span" display={{ base: "none", md: "inline" }}>&nbsp;|&nbsp;</Text>
              <Text as="span" display={{ base: "block", md: "inline" }}>PICKLEBALL</Text>
              <Text as="span" display={{ base: "none", md: "inline" }}>&nbsp;|&nbsp;</Text>
              <Text as="span" display={{ base: "block", md: "inline" }}>TABLE TENNIS</Text>
              <Text as="span" display={{ base: "none", md: "inline" }}>&nbsp;|&nbsp;</Text>
              <Text as="span" display={{ base: "block", md: "inline" }}>BADMINTON</Text>
              <Text as="span" display={{ base: "none", md: "inline" }}>&nbsp;|&nbsp;</Text>
              <Text as="span" display={{ base: "block", md: "inline" }}>SQUASH</Text>
            </Text>

            <Icon 
              as={FaMicrophone} 
              w={12} 
              h={12} 
              color={accentColor} 
              mb={4}
            />
            
            <Heading 
              as="h1" 
              size={{ base: "xl", md: "2xl" }}
              fontWeight="bold"
              lineHeight="1.2"
              px={{ base: 2, md: 0 }}
            >
              <Text as="span" color={accentColor}>Voice</Text> Activated<br />
              Scoring<br />
              while <Text as="span" color={accentColor}>On-Court</Text>
            </Heading>
            

            <Text fontSize={{ base: "lg", md: "xl" }} color="gray.300" maxW="2xl" px={{ base: 4, md: 0 }}>
              Control the scoring screen on your Mobile Device or Smart TV, using your <Text as="span" color="blue.300" display="inline">voice or click</Text>, with the most advanced <Text as="span" color="blue.300" display="inline">AI Scoring App</Text>.
            </Text>

            <Box w="100%" maxW="lg" mx="auto" pt={2} px={{ base: 2, md: 0 }}>
              <Box overflowX="auto">
                <Table variant="simple" size={{ base: "xs", md: "sm" }} colorScheme="gray" minW={{ base: "300px", md: "auto" }}>
                  <Thead>
                    <Tr>
                      <Th color="gray.300" fontSize={{ base: "xs", md: "sm" }} textAlign="center" colSpan={2}>VOICE COMMANDS:</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    <Tr>
                      <Td fontSize={{ base: "xs", md: "sm" }}><Text as="span" color="blue.300">"Yellow" or "Yankee"</Text></Td>
                      <Td fontSize={{ base: "xs", md: "sm" }}>to add a point for the yellow team/player</Td>
                    </Tr>
                    <Tr>
                      <Td fontSize={{ base: "xs", md: "sm" }}><Text as="span" color="blue.300">"Orange" or "Oscar"</Text></Td>
                      <Td fontSize={{ base: "xs", md: "sm" }}>to add an orange point</Td>
                    </Tr>
                    <Tr>
                      <Td fontSize={{ base: "xs", md: "sm" }}><Text as="span" color="blue.300">"Reverse point"</Text></Td>
                      <Td fontSize={{ base: "xs", md: "sm" }}>to undo a point</Td>
                    </Tr>
                    <Tr>
                      <Td fontSize={{ base: "xs", md: "sm" }}><Text as="span" color="blue.300">"Reset match"</Text></Td>
                      <Td fontSize={{ base: "xs", md: "sm" }}>to reset the match to 0-0</Td>
                    </Tr>
                  </Tbody>
                </Table>
              </Box>
            </Box>

            <VStack spacing={2} pt={4}>
            </VStack>

            <HStack spacing={6} pt={4}>
            </HStack>
          </VStack>

          {/* How It Works */}
          <VStack spacing={12} w="full">
            <Heading as="h2" size="lg" textAlign="center">
              How It Works
            </Heading>
            
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={8} w="full">
              <VStack spacing={4} textAlign="center">
                <Box
                  w={16}
                  h={16}
                  bg={accentColor}
                  borderRadius="full"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  fontSize="2xl"
                  fontWeight="bold"
                >
                  1
                </Box>
                <Heading as="h3" size="md">
                  Create Match
                </Heading>
                <Text color="gray.400">
                  <b><span style={{ color: '#3182ce' }}>Log in to start a new match</span></b><br />
                  <b><Text as="span" color="white">Individual use:</Text></b> Start a match on your phone or tablet.<br />
                  <b><Text as="span" color="white">Club use:</Text></b> Start a match on any compatible Smart TV's web browser (or use an Android, Fire, or Xiaomi stick).
                </Text>
              </VStack>
              <VStack spacing={4} textAlign="center">
                <Box
                  w={16}
                  h={16}
                  bg={accentColor}
                  borderRadius="full"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  fontSize="2xl"
                  fontWeight="bold"
                >
                  2
                </Box>
                <Heading as="h3" size="md">
                  Scan or Share QR Code
                </Heading>
                <Text color="gray.400">
                  <b><span style={{ color: '#3182ce' }}>No log in needed</span></b><br />
                  Connect another mobile device by Scanning the QR Code, to control and display the score.
                </Text>
              </VStack>
              <VStack spacing={4} textAlign="center">
                <Box
                  w={16}
                  h={16}
                  bg={accentColor}
                  borderRadius="full"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  fontSize="2xl"
                  fontWeight="bold"
                >
                  3
                </Box>
                <Heading as="h3" size="md">
                  Score with Voice or Click
                </Heading>
                <Text color="gray.400">
                  <b><span style={{ color: '#3182ce' }}>Highly recommended: Use a wire-less mic</span></b><br />
                  Controllers can use voice commands, or click, to control the score.
                </Text>
              </VStack>
            </SimpleGrid>
          </VStack>

          {/* How To Poster Button */}
          <Box w="full" textAlign="center" mb={8}>
            <Button
              as="a"
              href="/Club How To.pptx"
              download
              size="lg"
              colorScheme="blue"
              bg={accentColor}
              color="black"
              _hover={{ bg: '#2c5aa0' }}
              px={8}
              py={6}
              fontSize="lg"
              borderRadius="2px"
            >
              'How To' Poster .ppt
            </Button>
          </Box>

          {/* Advert Image */}
          <Box w="full" textAlign="center" mb={8}>
            <Image
              src="/Advert.jpg"
              alt="Advertisement"
              maxW="75%"
              h="auto"
              borderRadius="lg"
              boxShadow="md"
              border="2px solid"
              borderColor="gray.500"
              mx="auto"
              display="block"
            />
            <Text color="gray.300" fontSize="md" mt={4}>
              Club managers, customize and upload your own banners (x3) to advertize on your screens.
            </Text>
          </Box>

          {/* Testimonials Section */}
          <VStack spacing={10} w="full" py={8}>
            <Heading as="h2" size="lg" textAlign="center" color="white">
              Testimonials
            </Heading>
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={8} w="full">
              <Box bg={cardBg} p={6} borderRadius="lg" boxShadow="md" border="1px solid" borderColor="gray.700">
                <Text fontWeight="bold" fontSize="lg" mb={2} color={accentColor}>Club Owner</Text>
                <Text color="gray.200" fontStyle="italic" mb={4}>
                  "We use the app on a TV courtside, up against the glass, and now every match is tracked effortlessly. There's no need for players to stop and update a scoreboard. Members love how easy and hands-free it is. It's become a smart upgrade to the club experience, and has even helped settle disputes."
                </Text>
                <Text color="gray.400" fontSize="sm">- Simon L, Club Manager, Cape Town</Text>
              </Box>
              <Box bg={cardBg} p={6} borderRadius="lg" boxShadow="md" border="1px solid" borderColor="gray.700">
                <Text fontWeight="bold" fontSize="lg" mb={2} color={accentColor}>Competitive Player</Text>
                <Text color="gray.200" fontStyle="italic" mb={4}>
                  "This app has completely changed how we keep score in matches. Just shouting 'Red Point' or 'Yellow Point' keeps the game flowing, there's no distractions or confusion, and no more forgetting the score after a tough rally. It's like having an invisible umpire on court!"
                </Text>
                <Text color="gray.400" fontSize="sm">- Jessica M, Padel League</Text>
              </Box>
              <Box bg={cardBg} p={6} borderRadius="lg" boxShadow="md" border="1px solid" borderColor="gray.700">
                <Text fontWeight="bold" fontSize="lg" mb={2} color={accentColor}>Casual Doubles Team</Text>
                <Text color="gray.200" fontStyle="italic" mb={4}>
                  "We use our phones to track every match, it's so simple. One shout, and the score updates instantly across all linked devices. We've stopped arguing over what the score is or who's serving. It's amazing."
                </Text>
                <Text color="gray.400" fontSize="sm">- Dave & Ren, Social Padel Players</Text>
              </Box>
            </SimpleGrid>
          </VStack>


        </VStack>
      </Container>

      {/* Footer and Legal (Phase 6) */}
      <VStack spacing={8} w="full" py={12}>
        <Divider borderColor="gray.700" />
        <VStack spacing={6} textAlign="center">
          <Heading as="h2" size="lg">
            Transform Your Scoring!
          </Heading>
          <Text fontSize="lg" color="gray.300" maxW="2xl">
            Join thousands of players and clubs using AI-powered scoring. Just scan and start scoring.
          </Text>
        </VStack>
        <Text color="gray.500" fontSize="sm">
          <Link href="mailto:info@AiScoringApp.com" color="gray.400">
            info@AiScoringApp.com
          </Link>
        </Text>
        <Box as="footer" w="full" py={2} bg="transparent" textAlign="center" mt={0} mb={0} flexShrink={0}>
          <Text color="gray.400" fontSize="sm">
            By using this app, you agree to our{' '}
            <Link color="#3182ce" _hover={{ textDecoration: 'underline', color: '#2c5aa0' }} onClick={() => navigate('/terms')} cursor="pointer">
              Terms of Service
            </Link>{' '}and{' '}
            <Link color="#3182ce" _hover={{ textDecoration: 'underline', color: '#2c5aa0' }} onClick={() => navigate('/terms')} cursor="pointer">
              Privacy Policy
            </Link>.
          </Text>
          <Text color="gray.500" fontSize="sm" mt={4}>
            © 2026 AiScoringApp.com. All rights reserved. AiScoringApp™ is a trademark of AiScoringApp.com
          </Text>
        </Box>
      </VStack>

      {/* QR Code Scanner Modal (Phase 6) */}
      <QRCodeScanner isOpen={isScanOpen} onClose={() => setScanOpen(false)} />
      <FeedbackModal isOpen={isFeedbackOpen} onClose={() => setFeedbackOpen(false)} />
    </Box>
  )
}